import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Save, Loader2, Bot, Upload, Trash2, FileText } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

interface ChallengeConfig {
  id: string;
  system_prompt: string;
  model: string;
  temperature: number;
  max_tokens: number;
  min_rrf_score?: number;
}


interface RagDocument {
  id: string;
  name: string;
  file_url: string;
  created_at: string;
}

const AI_MODELS = [
  { value: 'google/gemini-2.5-flash', label: 'Gemini 2.5 Flash (recomendado)' },
  { value: 'google/gemini-2.5-pro', label: 'Gemini 2.5 Pro' },
  { value: 'google/gemini-2.5-flash-lite', label: 'Gemini 2.5 Flash Lite (económico)' },
  { value: 'openai/gpt-5-mini', label: 'GPT-5 Mini' },
  { value: 'openai/gpt-5', label: 'GPT-5' },
];

const DEFAULT_PROMPT = `Eres un Consultor Experto en Estrategia y Soluciones de Inteligencia Artificial dentro de la ruta LIDER IA. Tu objetivo es guiar al usuario paso a paso en la identificación de un reto dentro de su área de trabajo y proponer soluciones viables con Inteligencia Artificial (IA), basadas en cinco tipos de soluciones:

1. Creación de contenido con IA
2. Análisis de datos con IA
3. Asistente / Agente inteligente con IA
4. Automatización de procesos con IA
5. Creación de plataforma con IA (Nocode / VibeCoding)

Tu misión es transformar procesos ineficientes en proyectos de alto impacto tecnológico. Tu enfoque es "guiar haciendo": tú propones los análisis complejos basándote en la información del usuario, y él valida o ajusta.

Instrucciones generales:
- Usa markdown para formatear tus respuestas (tablas, negritas, listas, etc.)
- Haz preguntas una a la vez cuando necesites más información
- Sé empático, profesional y usa un tono cercano
- Cuando tengas suficiente información del reto (título, descripción, público y KPI), usa la herramienta extract_challenge_metadata para guardar los datos

---

🟢 INICIO: Contextualización y Diagnóstico Inicial

Saluda con energía y calidez. Explica que el objetivo es realizar un diagnóstico profundo para encontrar el reto con mayor potencial de retorno de inversión (ROI) mediante IA.

Instrucción de inicio:
"¡Hola! Soy tu consultor de LIDER IA. Mi objetivo es que hoy no solo encuentres un problema, sino que diseñemos una solución de IA que transforme tu área de trabajo. Para empezar, cuéntame: ¿En qué área trabajas y cuál es ese proceso crítico que hoy te genera fricción, lentitud o errores? Describe paso a paso cómo lo haces actualmente y qué herramientas usas."

---

🔍 FASE 1: Diagnóstico Profundo e Identificación del Reto

Una vez que el usuario describa su proceso, aplica las siguientes metodologías de forma proactiva. No le pidas al usuario que las haga; hazlas tú basándote en su descripción y preséntaselas para su aprobación. Antes de aplicar cada metodología, realiza de 1 a 3 preguntas clave si hace falta información.

1. **DOFA (SWOT):** Identifica Fortalezas, Oportunidades, Debilidades y Amenazas del proceso. **NO uses tabla markdown** (ralentiza el render del streaming). Presenta el DOFA como cuatro bloques con encabezado en negrita y bullets, cada bloque con 3-5 puntos cortos, exactamente en este orden y formato:

**Fortalezas**
- ...

**Oportunidades**
- ...

**Debilidades**
- ...

**Amenazas**
- ...

Cierra con: "¿Estás de acuerdo o quieres ajustar algo?"

2. **Value Stream Mapping (VSM) Simplificado:** Identifica dónde hay "desperdicio" (tiempos muertos, duplicidad, cuellos de botella). Presenta el VSM identificando los 'puntos de dolor' y pregunta: "¿Estás de acuerdo o quieres ajustar algo?"

3. **Lean Six Sigma (Enfoque en Variabilidad):** Detecta dónde el proceso falla o es inconsistente. Presenta el hallazgo y pregunta: "¿Estás de acuerdo o quieres ajustar algo?"

De acuerdo con el análisis, presenta al usuario un listado entre 1 a 5 retos identificados. Pregunta al usuario qué reto quiere abordar y continuar.

---

🛠️ FASE 2: Análisis Estructurado del Problema

Una vez validado el reto, analiza el reto utilizando estas 4 técnicas paso a paso. Muestra los resultados uno por uno:

1. **5W1H** (Who, What, Where, When, Why, How): Define el problema con precisión quirúrgica. Redacta los resultados y pregunta: "¿Este análisis refleja tu realidad o profundizamos en algún punto?"

2. **Espina de Pescado (Ishikawa):** Divide las causas en: Personas, Procesos, Tecnología y Datos. Redacta los resultados y pregunta: "¿Este análisis refleja tu realidad o profundizamos en algún punto?"

3. **Los 5 Porqués:** Llega a la causa raíz que la IA debe atacar. Redacta los resultados y pregunta: "¿Este análisis refleja tu realidad o profundizamos en algún punto?"

4. **Árbol de Problemas:** Visualiza las causas (raíces) y los efectos negativos (copa). Redacta los resultados y pregunta: "¿Este análisis refleja tu realidad o profundizamos en algún punto?"

En este punto, usa la herramienta extract_challenge_metadata para guardar título, descripción, público objetivo y KPI del reto seleccionado.

---

💡 FASE 3: Identificación de Soluciones con IA

Analiza toda la información del chat y presenta 5 opciones de proyectos (soluciones IA), una por cada categoría, y sugiere cuál encaja mejor con la causa raíz detectada:

1. **Automatización con IA:** Para tareas repetitivas y flujos de trabajo que tengan un trigger (evento definido).
2. **Asistente / Agente inteligente con IA:** Para interacción, consultas y soporte. Ejecución de tareas a través del chat con un agente.
3. **Análisis de Datos con IA:** Para creación de dashboards, encontrar patrones ocultos, realizar predicciones y toma de decisiones.
4. **Creación de Contenido con IA:** Para optimizar y facilitar la creación de todo tipo de contenidos, desde imágenes hasta videos. Mejorando fondo y forma.
5. **Creación de plataforma con IA (VibeCoding):** Para gestionar y optimizar procesos completos.

Intervención:
"Para resolver el problema [Descripción del problema], visualizo estas 5 opciones de soluciones de IA:
[5 ideas de soluciones IA una por cada categoría]
Yo te recomiendo la opción [X] por su facilidad e impacto. ¿Cuál prefieres seleccionar o te gustaría que te explique más a fondo alguna?"

---

🚀 FASE 4: Conversión a Proyecto

Explica al usuario que las posibles soluciones están disponibles en el botón de **"Soluciones"** en la parte superior del chat, y que desde ahí puede revisar cada solución en detalle y **convertir la que prefiera en un proyecto** para comenzar a desarrollarla.`;

export default function ChallengeAssistantConfigTab() {
  const qc = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load config
  const { data: config, isLoading } = useQuery({
    queryKey: ['challenge_assistant_config'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('challenge_assistant_config' as any)
        .select('*')
        .limit(1)
        .single();
      if (error) return { id: '', system_prompt: DEFAULT_PROMPT, model: 'google/gemini-2.5-flash', temperature: 0.7, max_tokens: 4096, min_rrf_score: 0 } as ChallengeConfig;
      return data as unknown as ChallengeConfig;
    },
  });

  // Load RAG documents
  const { data: documents = [], isLoading: docsLoading } = useQuery({
    queryKey: ['challenge_rag_documents'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('challenge_rag_documents' as any)
        .select('*')
        .order('created_at', { ascending: false });
      if (error) return [];
      return data as unknown as RagDocument[];
    },
  });

  const [form, setForm] = useState<Partial<ChallengeConfig> | null>(null);
  const rawActive = { ...{ system_prompt: DEFAULT_PROMPT, model: 'google/gemini-2.5-flash', temperature: 0.7, max_tokens: 4096, min_rrf_score: 0 }, ...config, ...form };
  const active = { ...rawActive, system_prompt: rawActive.system_prompt?.trim() ? rawActive.system_prompt : DEFAULT_PROMPT };
  const [uploading, setUploading] = useState(false);

  const saveMutation = useMutation({
    mutationFn: async (updates: Partial<ChallengeConfig>) => {
      if (config?.id) {
        const { error } = await supabase
          .from('challenge_assistant_config' as any)
          .update(updates)
          .eq('id', config.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('challenge_assistant_config' as any)
          .insert(updates);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['challenge_assistant_config'] });
      setForm(null);
      toast.success('Configuración guardada');
    },
    onError: () => toast.error('Error al guardar'),
  });

  const deleteDocMutation = useMutation({
    mutationFn: async (doc: RagDocument) => {
      // Delete from storage
      const path = doc.file_url.split('/challenge-rag/')[1];
      if (path) {
        await supabase.storage.from('challenge-rag').remove([path]);
      }
      // Delete from DB (chunks cascade)
      const { error } = await supabase
        .from('challenge_rag_documents' as any)
        .delete()
        .eq('id', doc.id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['challenge_rag_documents'] });
      toast.success('Documento eliminado');
    },
    onError: () => toast.error('Error al eliminar documento'),
  });

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['application/pdf', 'text/plain', 'text/markdown'];
    const allowedExts = ['.pdf', '.txt', '.md'];
    const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

    if (!allowedTypes.includes(file.type) && !allowedExts.includes(ext)) {
      toast.error('Solo se permiten archivos PDF, TXT o MD');
      return;
    }

    setUploading(true);
    try {
      // Upload to storage
      const fileName = `${Date.now()}_${file.name}`;
      const { error: uploadError } = await supabase.storage
        .from('challenge-rag')
        .upload(fileName, file);
      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage.from('challenge-rag').getPublicUrl(fileName);

      // Insert document record
      const { data: docRecord, error: insertError } = await supabase
        .from('challenge_rag_documents' as any)
        .insert({ name: file.name, file_url: urlData.publicUrl })
        .select()
        .single();
      if (insertError) throw insertError;

      // Extract text or send file_url for PDF extraction on backend
      const isPdf = ext === '.pdf';
      let textContent = '';
      if (!isPdf) {
        textContent = await file.text();
      }

      // Process chunks (backend extracts PDF text if file_url is provided)
      await supabase.functions.invoke('process-challenge-document', {
        body: {
          document_id: (docRecord as any).id,
          ...(isPdf ? { file_url: urlData.publicUrl } : { text_content: textContent }),
        },
      });

      qc.invalidateQueries({ queryKey: ['challenge_rag_documents'] });
      toast.success('Documento cargado y procesado');
    } catch (err: any) {
      console.error('Upload error:', err);
      toast.error('Error al subir el documento');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  if (isLoading) {
    return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6">
      {/* Agent Config */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bot className="w-5 h-5 text-primary" />
            Configuración del Asistente de Retos
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>System Prompt</Label>
            <Textarea
              className="min-h-[200px] font-mono text-xs"
              value={active.system_prompt}
              onChange={(e) => setForm(prev => ({ ...prev, system_prompt: e.target.value }))}
              placeholder="Instrucciones del asistente..."
            />
            <p className="text-xs text-muted-foreground">
              El contexto del usuario (perfil, diagnóstico) y los documentos RAG se agregan automáticamente.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Modelo IA</Label>
              <Select
                value={active.model}
                onValueChange={(v) => setForm(prev => ({ ...prev, model: v }))}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {AI_MODELS.map(m => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Temperatura: {active.temperature}</Label>
              <Slider
                value={[active.temperature]}
                onValueChange={([v]) => setForm(prev => ({ ...prev, temperature: v }))}
                min={0}
                max={1}
                step={0.1}
                className="mt-2"
              />
              <p className="text-xs text-muted-foreground">0 = preciso, 1 = creativo</p>
            </div>

            <div className="space-y-2">
              <Label>Max Tokens</Label>
              <Input
                type="number"
                value={active.max_tokens}
                onChange={(e) => setForm(prev => ({ ...prev, max_tokens: parseInt(e.target.value) || 4096 }))}
              />
            </div>
          </div>

          <div className="space-y-2 pt-2 border-t border-border/50">
            <Label>Umbral mínimo de relevancia RAG: {Number(active.min_rrf_score ?? 0).toFixed(3)}</Label>
            <Slider
              className="mt-2"
              min={0}
              max={0.05}
              step={0.001}
              value={[Number(active.min_rrf_score ?? 0)]}
              onValueChange={([v]) => setForm(prev => ({ ...prev, min_rrf_score: v }))}
            />
            <p className="text-xs text-muted-foreground">
              Si el mejor fragmento recuperado tiene score RRF por debajo de este umbral, el asistente responde con conocimiento general en lugar de citar documentos irrelevantes. 0 = sin filtro. Sugerido: 0.010–0.020.
            </p>
          </div>



          {form && (
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setForm(null)}>Cancelar</Button>
              <Button onClick={() => {
                if (form.system_prompt !== undefined && !form.system_prompt?.trim()) {
                  toast.error('El prompt no puede estar vacío');
                  return;
                }
                saveMutation.mutate(form);
              }} disabled={saveMutation.isPending}>
                {saveMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                <Save className="w-4 h-4 mr-2" />
                Guardar
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* RAG Documents */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              Documentos RAG
            </CardTitle>
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.txt,.md"
                className="hidden"
                onChange={handleFileUpload}
              />
              <Button
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                Subir Archivo
              </Button>
            </div>
          </div>
          <p className="text-sm text-muted-foreground">
            Sube documentos (PDF, TXT, MD) que el asistente usará como contexto para guiar a los participantes.
          </p>
        </CardHeader>
        <CardContent>
          {docsLoading ? (
            <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin" /></div>
          ) : documents.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">No hay documentos cargados</p>
          ) : (
            <div className="space-y-2">
              {documents.map((doc) => (
                <div key={doc.id} className="flex items-center justify-between p-3 rounded-lg border border-border/50 bg-muted/30">
                  <div className="flex items-center gap-3 min-w-0">
                    <FileText className="w-5 h-5 text-muted-foreground shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{doc.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {format(new Date(doc.created_at), "d MMM yyyy", { locale: es })}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => deleteDocMutation.mutate(doc)}
                    disabled={deleteDocMutation.isPending}
                  >
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
